import { useQuery } from '@tanstack/react-query';
import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FilterOperator, Comparator, SortOrder } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { FilterFieldConfig } from '@/hooks/useJsonFilters';
import { FilterGroup, ShiftSearchDto } from '@/lib/api/types.gen';
import { SearchCompanyShiftsQuery } from '@/lib/api/monitoring/shifts';
import { FullPageTable } from '@/components/tables/FullPageTable';
import { createTextColumn, createBadgeColumn } from '@/components/tables/column-helpers';
import { ColumnDef, SortingState } from '@tanstack/react-table';

// Team Shifts Table Data Type
interface TeamShiftTableData {
  shiftId: string;
  officerName: string;
  duration: string;
  startTime: string;
  endTimeOrStatus: React.ReactNode;
  start?: string;
  end?: string | null;
  lastSeen?: string;
}

// Filter configuration for team shifts
const filterConfig: FilterFieldConfig[] = [
  {
    type: 'text',
    name: 'officerName',
    label: 'Officer Name',
    field: 'monitoringOfficerName',
    comparator: Comparator.ILike,
    placeholder: 'Filter by officer name...',
    isSearch: true,
  },
  {
    type: 'date',
    name: 'startDateFrom',
    label: 'Start Date From',
    field: 'start',
    comparator: Comparator.GtEq,
    placeholder: 'Select start date',
  },
  {
    type: 'date',
    name: 'startDateTo',
    label: 'Start Date To',
    field: 'start',
    comparator: Comparator.LtEq,
    placeholder: 'Select end date',
  },
];

// Helper function to calculate shift duration
function calculateDuration(start?: string, end?: string | null, lastSeen?: string): string {
  const startDate = start ? new Date(start) : null;
  let endDate = end ? new Date(end) : null;

  if (!endDate && lastSeen) {
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    if (new Date(lastSeen) > tenMinutesAgo) {
      endDate = new Date();
    } else {
      endDate = new Date(lastSeen);
    }
  }

  if (startDate && endDate) {
    const durationMs = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0 || minutes > 0) {
      return `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
    }
    return '< 1m';
  }
  return 'N/A';
}

// Helper function to determine end time or status
function getEndTimeOrStatus(end?: string | null, lastSeen?: string): React.ReactNode {
  if (end) {
    return new Date(end).toLocaleString();
  }
  if (lastSeen) {
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    if (new Date(lastSeen) > tenMinutesAgo) {
      return <Badge className="bg-green-500/80 text-white">Active</Badge>;
    }
    return new Date(lastSeen).toLocaleString();
  }
  return 'N/A';
}

// Transform shift data for table display
function transformShiftData(shifts: ShiftSearchDto[]): TeamShiftTableData[] {
  return shifts.map((shift) => ({
    shiftId: shift.shiftId || '',
    officerName: shift.monitoringOfficerName || 'N/A',
    duration: calculateDuration(shift.start, shift.end, shift.lastSeen),
    startTime: shift.start ? new Date(shift.start).toLocaleString() : 'N/A',
    endTimeOrStatus: getEndTimeOrStatus(shift.end, shift.lastSeen),
    start: shift.start,
    end: shift.end,
    lastSeen: shift.lastSeen,
  }));
}

// Column definitions for team shifts table
const teamShiftsColumns: ColumnDef<TeamShiftTableData>[] = [
  createTextColumn({
    id: 'officerName',
    label: 'Officer Name',
    accessorKey: 'officerName',
    sortable: true,
    cell: (value) => <span className="font-semibold">{value}</span>,
  }),
  createTextColumn({
    id: 'duration',
    label: 'Duration',
    accessorKey: 'duration',
    sortable: false,
    cell: (value) => <span>{value}</span>,
  }),
  createTextColumn({
    id: 'startTime',
    label: 'Start Time',
    accessorKey: 'startTime',
    sortable: true,
    cell: (value) => <span className="text-gray-300">{value}</span>,
  }),
  {
    id: 'endTimeOrStatus',
    accessorKey: 'endTimeOrStatus',
    enableHiding: true,
    meta: {
      label: 'End Time / Status',
      align: 'left',
    },
    header: 'End Time / Status',
    cell: ({ row }) => row.original.endTimeOrStatus,
  },
];

export function TeamShiftsTab() {
  const { teamId: companyId } = useParams<{ teamId: string }>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'startTime', desc: true }]);

  const [filterState, setFilterState] = useState<{
    filterOperator: FilterOperator;
    filterFilterGroups: FilterGroup[];
    values: Record<string, string | Date | undefined>;
  }>({ filterOperator: FilterOperator.And, filterFilterGroups: [], values: {} });

  // Get sorting parameters for API
  const sortField = sorting[0]?.id === 'startTime' ? 'start' : 
                   sorting[0]?.id === 'officerName' ? 'monitoringOfficerName' : 'start';
  const sortDirection = sorting[0]?.desc ? SortOrder.Descending : SortOrder.Ascending;

  const shiftsQuery = useQuery({
    ...SearchCompanyShiftsQuery({
      companyId: companyId ?? '',
      pageNumber: page,
      pageSize,
      sortBy: sortField,
      sortDir: sortDirection,
      ...filterState,
    }),
    enabled: !!companyId,
  });

  const shifts = shiftsQuery.data?.rows ?? [];
  const totalRows = shiftsQuery.data?.totalRows ?? 0;

  // Transform raw shift data for table display
  const tableData = useMemo(() => transformShiftData(shifts), [shifts]);

  // Handle sorting changes
  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);
    setPage(1); // Reset to first page when sorting changes
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
  };

  // Handle filter changes
  const handleFiltersChange = (newFilterState: {
    filterOperator: FilterOperator;
    filterFilterGroups: FilterGroup[];
    values: Record<string, string | Date | undefined>;
  }) => {
    setFilterState(newFilterState);
    setPage(1); // Reset to first page when filters change
  };

  return (
    <FullPageTable
      title="Team Shifts"
      columns={teamShiftsColumns}
      data={tableData}
      loading={shiftsQuery.isLoading}
      error={shiftsQuery.error}
      page={page}
      pageSize={pageSize}
      totalCount={totalRows}
      onPageChange={setPage}
      onPageSizeChange={handlePageSizeChange}
      pageSizeOptions={[5, 10, 20, 100]}
      filterConfig={filterConfig}
      onFiltersChange={handleFiltersChange}
      stateKey="team-shifts-table"
      defaultColumnVisibility={{}}
      defaultSorting={[{ id: 'startTime', desc: true }]}
      onSortingChange={handleSortingChange}
      emptyMessage="No shifts found."
    />
  );
}
